import { motion } from "framer-motion";
import { Percent, Phone, Map, Zap, Users, MessageCircle, Star, Building2, TrendingUp, Gift, Crown } from "lucide-react";

const benefits = [
  { 
    icon: Gift, 
    title: "Founding 50 Advantage", 
    desc: "Founding 50 members receive permanent referral and affiliate advantages beyond the standard Argus program.",
    highlight: true,
    tag: "EXCLUSIVE"
  },
  { 
    icon: Percent, 
    title: "$374/mo Locked Forever", 
    desc: "While others pay $499+/mo, your rate never increases. Unlimited locations, one flat price. Forever.",
    highlight: false
  },
  { 
    icon: MessageCircle, 
    title: "Private Founder WhatsApp", 
    desc: "Direct access to the founding team and fellow operators. Feature requests, support, and strategy — in real-time.",
    highlight: false
  },
  { 
    icon: Crown, 
    title: "Founder Badge & Early Access", 
    desc: "Be first to try new features before anyone else, plus a permanent 'Founding Member' badge in your dashboard.",
    highlight: false
  },
  { 
    icon: Map, 
    title: "Direct Product Influence", 
    desc: "Your feedback shapes what we build. Founders get a direct line to the team — your restaurant's needs drive our roadmap.",
    highlight: false
  },
];

const BeforeAfterSection = () => (
  <motion.div 
    className="mb-16"
    initial={{ opacity: 0, y: 20 }} 
    whileInView={{ opacity: 1, y: 0 }} 
    viewport={{ once: true }}
  >
    <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
      {/* BEFORE Column */}
      <div className="glass-card rounded-xl p-8 border border-red-500/30 bg-gradient-to-br from-red-950/20 to-transparent">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
            <span className="text-red-400 text-lg">✗</span>
          </div>
          <h3 className="font-inter text-xl font-bold text-red-400">Before Argus Xenia</h3>
        </div>
        <ul className="space-y-4">
          {[
            "Missed 30% of calls → $18,000+/year lost",
            "8 hours/week on scheduling chaos",
            "Recipe costs unknown → silent margin leaks",
            "Training inconsistent → high turnover",
            "Safety violations surprise you"
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-slate-400 text-sm">
              <span className="text-red-400 mt-0.5">•</span>
              {item}
            </li>
          ))}
        </ul>
      </div>

      {/* AFTER Column */}
      <div className="glass-card rounded-xl p-8 border border-emerald-500/30 bg-gradient-to-br from-emerald-950/20 to-transparent gold-glow">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <span className="text-emerald-400 text-lg">✓</span>
          </div>
          <h3 className="font-inter text-xl font-bold text-emerald-400">After Argus Xenia</h3>
        </div>
        <ul className="space-y-4">
          {[
            "AI answers every call → $0 lost revenue",
            "Self-driving ops → 12+ hours back/week",
            "Real-time cost analysis → 15%+ margin gains",
            "Voice-built SOPs → training in minutes",
            "24/7 compliance AI → zero surprise inspections"
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-white text-sm">
              <span className="text-emerald-400 mt-0.5">✓</span>
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </motion.div>
);

const FoundingAdvantageCallout = () => (
  <motion.div 
    className="max-w-4xl mx-auto mb-16"
    initial={{ opacity: 0, scale: 0.95 }} 
    whileInView={{ opacity: 1, scale: 1 }} 
    viewport={{ once: true }}
  >
    <div className="glass-card rounded-2xl p-8 border-2 border-gold/40 bg-gradient-to-br from-gold/5 to-transparent gold-glow relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute -right-20 -top-20 w-64 h-64 bg-gold/5 rounded-full blur-3xl" />
      
      <div className="relative z-10 text-center">
        <div className="inline-flex items-center gap-2 bg-gold/20 px-4 py-2 rounded-full mb-4">
          <TrendingUp className="w-4 h-4 text-gold" />
          <span className="text-gold text-sm font-bold tracking-wide">FOUNDER EXCLUSIVE</span>
        </div>
        
        <h3 className="font-inter text-2xl md:text-3xl font-black text-white mb-4">
          Founding <span className="text-gold">50</span> Advantage
        </h3>
        
        <p className="text-slate-300 max-w-2xl mx-auto mb-6 text-base md:text-lg leading-relaxed">
          Founding 50 members receive permanent referral and affiliate advantages beyond the standard Argus program.
        </p>
        
        <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
          <div className="bg-indigo-950/50 rounded-lg p-4 border border-gold/10">
            <p className="text-lg font-black text-gold leading-tight">FOUNDING<br/>50</p>
            <p className="text-xs text-slate-400 mt-1">Founder Status</p>
          </div>
          <div className="bg-indigo-950/50 rounded-lg p-4 border border-gold/10">
            <p className="text-2xl font-black text-white">50</p>
            <p className="text-xs text-slate-400">Founding Spots</p>
          </div>
          <div className="bg-indigo-950/50 rounded-lg p-4 border border-gold/10">
            <p className="text-2xl font-black text-emerald-400">∞</p>
            <p className="text-xs text-slate-400">Locations</p>
          </div>
        </div>
        
        <p className="text-sm text-slate-500 mt-6">
          Permanent advantages reserved for the Founding 50.
        </p>
      </div>
    </div>
  </motion.div>
);

// Founding Member Badge Showcase
const FounderBadgeShowcase = () => (
  <motion.div 
    className="max-w-4xl mx-auto mb-16"
    initial={{ opacity: 0, y: 20 }} 
    whileInView={{ opacity: 1, y: 0 }} 
    viewport={{ once: true }}
  >
    <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12">
      {/* The Badge */}
      <motion.div 
        className="relative"
        whileHover={{ scale: 1.05 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        {/* Glow effect */}
        <div className="absolute inset-0 bg-gold/25 blur-3xl rounded-full scale-110" />
        
        {/* Badge Container */}
        <div className="relative w-48 h-48 md:w-56 md:h-56">
          {/* Gold Laurel Wreath Image */}
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/257fa457-b138-4585-bcd3-2a9fdfdbe453/images/6d2720ba399591e7d3db145154b0bf1ed8560acfaf59418fc5093b62862d5949.png" 
            alt="Gold Laurel Wreath"
            className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_0_15px_rgba(212,175,55,0.4)]"
          />
          
          {/* Inner circle with content */}
          <div className="absolute inset-[18%] rounded-full bg-gradient-to-br from-[#030B36] via-[#061344] to-[#030A2E] border-2 border-gold/50 flex items-center justify-center shadow-[0_0_40px_rgba(212,175,55,0.15),inset_0_0_20px_rgba(0,0,0,0.5)]">
            <div className="text-center px-2">
              {/* Shield icon */}
              <div className="mb-1.5">
                <svg className="w-8 h-8 md:w-10 md:h-10 mx-auto" viewBox="0 0 80 80" fill="none">
                  <path 
                    d="M40 8L14 22V44C14 59 25.5 72 40 76C54.5 72 66 59 66 44V22L40 8Z" 
                    stroke="#D4AF37" 
                    strokeWidth="2.5" 
                    fill="rgba(212,175,55,0.08)" 
                  />
                  <path 
                    d="M26 44C26 44 32 35 40 35C48 35 54 44 54 44C54 44 48 53 40 53C32 53 26 44 26 44Z" 
                    stroke="#00FF41" 
                    strokeWidth="1.5" 
                    fill="none" 
                    opacity="0.9" 
                  />
                  <circle cx="40" cy="44" r="5" stroke="#00FF41" strokeWidth="1.5" fill="rgba(0,255,65,0.12)" />
                  <circle cx="40" cy="44" r="2.5" fill="#00FF41" />
                </svg>
              </div>
              <p className="font-inter text-[9px] md:text-[10px] font-bold tracking-[0.12em] text-gold uppercase">
                Founding 50
              </p>
              <p className="font-inter text-base md:text-lg font-black text-white leading-tight -mt-0.5">
                MEMBER
              </p>
              {/* 5 Stars */}
              <div className="mt-1 flex items-center justify-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-2.5 h-2.5 md:w-3 md:h-3 text-gold fill-gold" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Badge Description */}
      <div className="text-center md:text-left max-w-sm">
        <h4 className="font-inter text-xl md:text-2xl font-bold text-white mb-3">
          Your Badge. <span className="text-gold">Forever.</span>
        </h4>
        <p className="text-slate-400 text-sm md:text-base leading-relaxed mb-4">
          This badge lives permanently in your dashboard — a mark of trust from the earliest days. 
          When we're the #1 restaurant OS, everyone will know you helped build it.
        </p>
        <div className="flex flex-wrap gap-2 justify-center md:justify-start">
          <span className="px-3 py-1 bg-gold/10 border border-gold/30 rounded-full text-xs text-gold font-medium">
            Early Access
          </span>
          <span className="px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-xs text-emerald-400 font-medium">
            Lifetime Status
          </span>
          <span className="px-3 py-1 bg-indigo-500/10 border border-indigo-500/30 rounded-full text-xs text-indigo-400 font-medium">
            Priority Support
          </span>
        </div>
      </div>
    </div>
  </motion.div>
);

export const FoundingBenefits = ({ remaining }) => {
  const soldOut = remaining !== undefined && remaining <= 0;

  return (
    <section data-testid="benefits-section" className="py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="font-inter text-xs font-bold tracking-[0.3em] uppercase text-gold">
            {soldOut ? "General Waitlist Open" : "Founding Member Perks"}
          </span>
          <h2 className="font-inter text-4xl md:text-5xl font-black text-white mt-4">
            {soldOut ? "Join the Waitlist" : <>The Founding <span className="text-gold">50</span></>}
          </h2>
          <p className="text-base md:text-lg text-slate-400 mt-4 max-w-2xl mx-auto">
            {soldOut
              ? "All founding spots have been claimed. Join our general waitlist for early access."
              : "Not just early access. Founding 50 members receive permanent advantages that later subscribers will not."}
          </p>
        </motion.div>

        {!soldOut && (
          <>
            {/* Before/After Comparison */}
            <BeforeAfterSection />
            
            {/* Founding 50 Advantage Callout */}
            <FoundingAdvantageCallout />
            
            {/* Founding Member Badge Showcase */}
            <FounderBadgeShowcase />
            
            {/* 5 Benefits Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((b, i) => {
                const Icon = b.icon;
                return (
                  <motion.div 
                    key={b.title} 
                    data-testid={`benefit-card-${i}`}
                    className={`glass-card rounded-xl p-6 border hover:-translate-y-1 transition-transform duration-300 relative ${
                      b.highlight ? "border-gold/40 gold-glow lg:col-span-1 md:col-span-2" : "border-gold/10"
                    }`}
                    initial={{ opacity: 0, y: 20 }} 
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }} 
                    transition={{ delay: i * 0.08 }}
                  >
                    {b.tag && (
                      <div className="absolute -top-3 left-6">
                        <span className="bg-gold text-indigo-900 text-xs font-bold px-3 py-1 font-inter rounded-sm">
                          {b.tag}
                        </span>
                      </div>
                    )}
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                      b.highlight ? "bg-gold/20" : "bg-gold/10"
                    }`}>
                      <Icon className={`w-6 h-6 ${b.highlight ? "text-gold" : "text-gold/80"}`} />
                    </div>
                    <h3 className="font-inter text-lg font-bold text-white mb-2">{b.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{b.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
};
